import { isNil } from '@sixfold/typed-primitives';
import classnames from 'classnames';
import { Component, type ReactNode } from 'react';

export type Props = {
  children?: ReactNode;
  loadMoreEntries?: () => Promise<unknown> | undefined;
};

export interface DisplayProps {
  triggerOnWindow?: boolean;
  threshold?: number;
  className?: string;
}

export interface State {
  isLoading: boolean;
  hasInitialAutoLoaded: boolean;
}

const getDistanceLeftToScroll = (window: Window, element: HTMLDivElement | null, triggerOnWindow: boolean = true) => {
  if (element === null) {
    return 0;
  }

  if (triggerOnWindow) {
    return element.getBoundingClientRect().top + element.scrollHeight - window.innerHeight;
  }

  return element.scrollHeight - element.scrollTop - element.clientHeight;
};

export class InfiniteScroll extends Component<Props & DisplayProps, State> {
  private container: HTMLDivElement | null;
  private isMounted: boolean = false;

  static defaultProps: Partial<DisplayProps> = { triggerOnWindow: true, threshold: 250 };

  constructor(props: Props & DisplayProps) {
    super(props);

    this.container = null;
    this.scrollListener = this.scrollListener.bind(this);
    this.state = { isLoading: false, hasInitialAutoLoaded: false };
  }

  componentDidMount() {
    this.isMounted = true;
    this.attachScrollListener();

    if (this.shouldLoadMore()) {
      this.loadMore();
    }
  }

  componentDidUpdate() {
    const { isLoading } = this.state;

    if (!isLoading) {
      this.attachScrollListener();

      if (this.shouldLoadMore()) {
        this.loadMore();
      }
    }
  }

  scrollListener() {
    window.requestAnimationFrame(() => {
      if (isNil(this.props.threshold)) {
        return;
      }

      if (getDistanceLeftToScroll(window, this.container, this.props.triggerOnWindow) < this.props.threshold) {
        this.loadMore();
      }
    });
  }

  loadMore() {
    this.detachScrollListener();
    const promise = this.props.loadMoreEntries?.();

    if (promise !== undefined) {
      this.setState({ isLoading: true });

      promise
        .catch(() => {
          // Error is silently handled to allow retry on next scroll
        })
        .finally(() => {
          if (this.isMounted) {
            this.setState({ isLoading: false, hasInitialAutoLoaded: true });
          }
        });
    }
  }

  shouldLoadMore(): boolean {
    if (this.container === null || isNil(this.props.threshold) || this.props.loadMoreEntries === undefined) {
      return false;
    }

    if (this.props.triggerOnWindow === false) {
      const hasOverflow = this.container.scrollHeight > this.container.clientHeight;

      if (hasOverflow) {
        const distanceToBottom = getDistanceLeftToScroll(window, this.container, false);

        return distanceToBottom < this.props.threshold;
      }

      return !this.state.hasInitialAutoLoaded;
    }

    return getDistanceLeftToScroll(window, this.container, this.props.triggerOnWindow) < this.props.threshold;
  }

  attachScrollListener() {
    if (this.props.loadMoreEntries === undefined) {
      return;
    }

    if (this.props.triggerOnWindow === false) {
      if (this.container !== null) {
        this.container.addEventListener('scroll', this.scrollListener);
        this.container.addEventListener('resize', this.scrollListener);
      }
    } else {
      window.addEventListener('scroll', this.scrollListener);
      window.addEventListener('resize', this.scrollListener);
    }
  }

  detachScrollListener() {
    if (this.props.loadMoreEntries === undefined) {
      return;
    }

    if (this.props.triggerOnWindow === false) {
      if (this.container !== null) {
        this.container.removeEventListener('scroll', this.scrollListener);
        this.container.removeEventListener('resize', this.scrollListener);
      }
    } else {
      window.removeEventListener('scroll', this.scrollListener);
      window.removeEventListener('resize', this.scrollListener);
    }
  }

  componentWillUnmount() {
    this.isMounted = false;
    this.detachScrollListener();
  }

  render() {
    const { children, className } = this.props;
    const { isLoading } = this.state;

    if (isNil(children)) {
      return null;
    }

    return (
      <div
        className={className}
        ref={(ref) => {
          this.container = ref;
        }}>
        {children}
        <div className={classnames('ui centered small inline loader', { active: isLoading })} />
      </div>
    );
  }
}
